'use client'

import { useEffect } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react'
import type { VoiceLang } from '@/lib/detect-lang'

// Кнопки голосового ввода — по одной на язык: название языка на нём самом,
// чтобы турецкую кнопку узнавали сразу, а подпись объясняет жест.
const VOICE_BUTTONS: { lang: VoiceLang; title: string; sub: string; aria: string }[] = [
  { lang: 'ru-RU', title: 'Русский', sub: 'держите и говорите', aria: 'по-русски' },
  { lang: 'tr-TR', title: 'Türkçe', sub: 'держите и говорите', aria: 'по-турецки' },
]

// Общие для «Спроси Turkıdo» (tutor-qa.tsx) и «Разговора с AI» (ai-chat.tsx)
// кнопки «держите и говорите»: ученик сам выбирает язык записи (Web Speech API
// распознаёт ровно один язык за сеанс). Раскладка inline-стилями — не зависит
// от устаревшего CSS-бандла на телефоне.
export function VoiceHoldButtons({
  activeLang,
  disabled,
  onStart,
  onEnd,
  idleHint,
  compact,
  only,
}: {
  activeLang: VoiceLang | null
  disabled: boolean
  onStart: (lang: VoiceLang) => void
  onEnd: () => void
  // Подпись под кнопками, пока запись не идёт (у «Спроси Turkido» — заменит
  // запись текст в поле или вставится в место курсора).
  idleHint?: string
  // Окно «Спроси Turkido»: кнопки всегда рядом и в одну строку, подсказка в
  // одну строку - на телефоне с крупным текстом блок занимал полэкрана.
  compact?: boolean
  // Одна кнопка вместо двух: шаг "Сыграйте диалог" (dialogue-play.tsx) ждёт
  // только турецкую речь.
  only?: VoiceLang
}) {
  // Пока держат кнопку, Chrome на Android выделял ближайший текст (подпись под
  // кнопками, реплики чата) и открывал меню "Копировать" и поиск Google
  // (скриншот владельца 16.10.2026). Во время записи выделение запрещено на
  // всей странице, сам блок не выделяется никогда.
  const recording = activeLang !== null
  useEffect(() => {
    if (!recording) return
    const block = (e: Event) => e.preventDefault()
    document.addEventListener('selectstart', block)
    document.addEventListener('contextmenu', block)
    window.getSelection()?.removeAllRanges()
    return () => {
      document.removeEventListener('selectstart', block)
      document.removeEventListener('contextmenu', block)
    }
  }, [recording])

  function pointerDown(lang: VoiceLang) {
    return (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (e.button !== 0) return
      // Захват указателя: палец легко съезжает с кнопки во время удержания, и
      // без него pointerup прилетел бы другому элементу — запись осталась бы
      // висеть включённой.
      e.currentTarget.setPointerCapture(e.pointerId)
      onStart(lang)
    }
  }

  // Кнопка «пока держат» с клавиатуры недоступна через click — Enter/Space
  // шлют только его, без pointer-событий. Держат клавишу — идёт запись.
  function keyDown(lang: VoiceLang) {
    return (e: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (e.key !== ' ' && e.key !== 'Enter') return
      e.preventDefault()
      if (e.repeat) return
      onStart(lang)
    }
  }

  function keyUp(e: ReactKeyboardEvent<HTMLButtonElement>) {
    if (e.key !== ' ' && e.key !== 'Enter') return
    onEnd()
  }

  return (
    <div
      className="@container"
      onContextMenu={(e) => e.preventDefault()}
      style={{ display: 'flex', flexDirection: 'column', gap: 6, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
    >
      {/* Уже 300px (узкий телефон, крупный текст в настройках) кнопки друг под
          другом: рядом подпись "держите и говорите" сжималась в столбик. */}
      {/* compact (окно «Спроси Turkido»): уже 16.5rem (320-375 px в «Крупном») значок микрофона
          вылезал за левый край кнопки, подпись упиралась в правый, поэтому тоже друг под другом (тикет 49). */}
      <div className={only ? 'grid grid-cols-1' : compact ? 'grid grid-cols-1 @[16.5rem]:grid-cols-2' : 'grid grid-cols-1 @[18.75rem]:grid-cols-2'} style={{ gap: 8 }}>
        {VOICE_BUTTONS.filter((b) => !only || b.lang === only).map((b) => {
          const active = activeLang === b.lang
          const dimmed = activeLang !== null && !active
          return (
            <button
              key={b.lang}
              type="button"
              className="tk-mic-btn tk-pill"
              data-on={active || undefined}
              disabled={disabled || dimmed}
              onPointerDown={pointerDown(b.lang)}
              onPointerUp={onEnd}
              onPointerCancel={onEnd}
              onKeyDown={keyDown(b.lang)}
              onKeyUp={keyUp}
              onContextMenu={(e) => e.preventDefault()}
              aria-pressed={active}
              aria-label={`Удерживайте и говорите ${b.aria}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                minHeight: compact ? 44 : 52,
                padding: compact ? '6px 10px' : '8px 12px',
                borderRadius: 14,
                whiteSpace: 'normal',
                touchAction: 'none',
              }}
            >
              <span
                aria-hidden
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 9999,
                  flexShrink: 0,
                  background: active ? 'var(--tk-gold-accent)' : 'oklch(0.58 0.19 258 / 0.35)',
                  color: active ? 'oklch(0.2 0.05 25)' : 'white',
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  width="16"
                  height="16"
                  className={active ? 'animate-pulse' : undefined}
                >
                  <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z" />
                  <path d="M19 11a7 7 0 0 1-14 0M12 18v3" />
                </svg>
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15, textAlign: 'left' }}>
                <span style={{ fontSize: 16, fontWeight: 700 }}>{active ? 'Слушаю...' : b.title}</span>
                {/* Цвет — от кнопки, чтобы подпись белела вместе с ней. */}
                {!compact && (
                  <span style={{ fontSize: 14, fontWeight: 400, opacity: 0.85 }}>
                    {active ? 'отпустите, когда закончите' : b.sub}
                  </span>
                )}
              </span>
            </button>
          )
        })}
      </div>
      {/* idleHint="" - подсказка в покое не нужна, строку не рисуем. */}
      {(activeLang || idleHint !== '') && (
      <div className={compact ? 'tk-hold-hint text-[13px] leading-[1.3]' : 'tk-hold-hint text-[15px] leading-[1.4]'} style={{ color: activeLang ? 'var(--tk-gold-accent)' : 'oklch(0.86 0.03 240)' }}>
        {activeLang
          ? compact || only
            ? 'Говорите, потом отпустите кнопку.'
            : `Говорите ${activeLang === 'ru-RU' ? 'по-русски' : 'по-турецки'}. Текст появится в поле, потом отправьте его.`
          : (idleHint ?? (compact ? 'Держите кнопку языка и говорите.' : 'Держите кнопку нужного языка и говорите. Отпустите, и текст появится в поле.'))}
      </div>
      )}
    </div>
  )
}
